import type {
    ActionLoad,
    HttpPort,
    IActionService,
    Json,
    ListQuery,
    ListResult,
    ModelFieldSpec,
    ModelViewLoad,
    RecordData,
    RelatedCountQuery,
    RelatedCountResult,
    SavedFilter,
    ServerRecordGroup,
} from "../contracts";
import { listQueryToSearchParams } from "./query";
import { mapActionLoad, mapFieldDef, mapSavedFilter } from "./mappers";

const slug = (s: string) => encodeURIComponent(s);

export class ActionService implements IActionService {
    constructor(private readonly http: HttpPort) {}

    async loadAction(actionSlug: string): Promise<ActionLoad> {
        return mapActionLoad(await this.http.get(`/api/web/${slug(actionSlug)}/load`));
    }

    async loadModelView(modelKey: string, viewType: "list" | "form" | "kanban" | "search" | "clientaction"): Promise<ModelViewLoad | null> {
        const res = await this.http.get<{
            status?: string;
            model_key?: string;
            view_type?: string;
            field_defs?: unknown[];
            view?: unknown;
        }>(`/api/web/_model/${slug(modelKey)}/views/${slug(viewType)}`);
        if (!res || res.status !== "ok") return null;
        return {
            modelKey: String(res.model_key ?? modelKey),
            viewType: String(res.view_type ?? viewType),
            fieldDefs: (res.field_defs ?? []).map(mapFieldDef),
            view: res.view ?? null,
        };
    }

    async readRecordByModel(modelKey: string, recordUuid: string, viewType: "form" | "list" = "form"): Promise<RecordData> {
        const res = await this.http.get<{ record?: RecordData } | RecordData>(
            `/api/web/_model/${slug(modelKey)}/records/${encodeURIComponent(recordUuid)}?view_type=${encodeURIComponent(viewType)}`,
        );
        const record = (res as { record?: RecordData }).record;
        return record ?? (res as RecordData);
    }

    async searchRecordsByModel(modelKey: string, query: ListQuery, viewType: "list" | "form" = "list"): Promise<ListResult> {
        const res = await this.http.post<{ records?: RecordData[]; total?: number }>(
            `/api/web/_model/${slug(modelKey)}/records/search`,
            { ...query, view_type: viewType },
        );
        return { records: res.records ?? [], total: res.total, groups: undefined };
    }

    async listRecords(actionSlug: string, query: ListQuery): Promise<ListResult> {
        const res = await this.http.get<{
            records?: RecordData[];
            total?: number;
            grouped?: boolean;
            groups?: ServerRecordGroup[];
        }>(
            `/api/web/${slug(actionSlug)}/records${listQueryToSearchParams(query)}`,
        );
        return {
            records: res.records ?? [],
            total: res.total,
            grouped: res.grouped === true,
            groups: res.grouped === true ? res.groups ?? [] : undefined,
        };
    }

    async readRecord(actionSlug: string, recordUuid: string, viewType: string): Promise<RecordData> {
        const res = await this.http.get<{ record?: RecordData } | RecordData>(
            `/api/web/${slug(actionSlug)}/records/${encodeURIComponent(recordUuid)}?view_type=${encodeURIComponent(viewType)}`,
        );
        const record = (res as { record?: RecordData }).record;
        return record ?? (res as RecordData);
    }

    async runButton(actionSlug: string, recordUuid: string, commandName: string, context?: Json): Promise<Json> {
        return this.http.post<Json>(
            `/api/web/${slug(actionSlug)}/${encodeURIComponent(recordUuid)}/${encodeURIComponent(commandName)}`,
            context ?? {},
        );
    }

    /** POST /api/records/{model_key} — create a new record. Wraps the CRUD
        kernel via foundation.base.crud_routes; backend dispatches `ede.create`.

        Same wire-shape rationale as updateRecord — kernel returns the
        minimal scalar record; this boundary refetches through
        `presentation.read_record_by_model` so the caller gets back a
        FULL enriched record (relation expansion + display_names) without
        any post-create patching at the consumer. */
    async createRecord(modelKey: string, values: Record<string, Json>): Promise<RecordData> {
        const res = await this.http.post<{ record?: RecordData } | RecordData>(
            `/api/records/${slug(modelKey)}`,
            { values },
        );
        const created = (res as { record?: RecordData }).record ?? (res as RecordData);
        const recordUuid = typeof created?.record_uuid === "string" ? created.record_uuid : null;
        if (!recordUuid) return created;
        return this.readRecordByModel(modelKey, recordUuid, "form");
    }

    /** PUT /api/records/{model_key}/{record_id} — update an existing record.
        Backend dispatches `ede.update`; payload is the dirty-filtered values
        from `buildSaveValues`.

        The kernel's `ede.update` response is intentionally minimal — scalar
        columns only, no O2M / M2M expansion. That keeps the CRUD command
        contract tight: a write returns what was written. But callers (form
        views, draft state) need the COMPLETE post-write record so relation
        UIs (RelationListView) don't go empty after save. The wire-shape
        translation belongs here at the action-service boundary: do the
        write, then refetch through `presentation.read_record_by_model`
        so the caller gets back the fully enriched record (relation
        expansion + display_name resolution). The kernel stays pure; the
        frontend stays patch-free. */
    async updateRecord(modelKey: string, recordId: string, values: Record<string, Json>): Promise<RecordData> {
        await this.http.put(
            `/api/records/${slug(modelKey)}/${encodeURIComponent(recordId)}`,
            { values },
        );
        return this.readRecordByModel(modelKey, recordId, "form");
    }

    async searchRelated(
        actionSlug: string,
        fieldName: string,
        q: string,
        opts: { domain?: string | null; limit?: number } = {},
    ): Promise<RecordData[]> {
        const qs = new URLSearchParams({ q });
        if (opts.limit != null) qs.set("limit", String(opts.limit));
        if (opts.domain) qs.set("domain", opts.domain);
        const res = await this.http.get<{ records?: RecordData[] } | RecordData[]>(
            `/api/web/${slug(actionSlug)}/related/${encodeURIComponent(fieldName)}?${qs.toString()}`,
        );
        return Array.isArray(res) ? res : (res.records ?? []);
    }

    async listFilters(actionSlug: string): Promise<SavedFilter[]> {
        const res = await this.http.get<{ records?: unknown[]; filters?: unknown[] } | unknown[]>(
            `/api/web/${slug(actionSlug)}/filters`,
        );
        const arr = Array.isArray(res) ? res : (res.records ?? res.filters ?? []);
        return arr.map(mapSavedFilter);
    }

    async createFilter(actionSlug: string, payload: { name: string; domain: string; groupby?: string }): Promise<SavedFilter> {
        const res = await this.http.post(`/api/web/${slug(actionSlug)}/filters`, { ...payload, filter_type: "favorite" });
        return mapSavedFilter(res);
    }

    async deleteFilter(actionSlug: string, filterId: string): Promise<void> {
        await this.http.delete(`/api/web/${slug(actionSlug)}/filters/${encodeURIComponent(filterId)}`);
    }

    async modelFields(modelKey: string): Promise<ModelFieldSpec | null> {
        const res = await this.http.get<{ label?: string; fields?: unknown[] }>(
            `/api/web/_model/${slug(modelKey)}/fields`,
        );
        if (!res || !Array.isArray(res.fields)) return null;
        return {
            label: String(res.label ?? modelKey),
            fields: res.fields.map((raw) => {
                const f = (raw ?? {}) as Record<string, unknown>;
                const out: ModelFieldSpec["fields"][number] = {
                    name: String(f.name ?? ""),
                    label: String(f.label ?? f.name ?? ""),
                    type: String(f.type ?? "char"),
                };
                if (typeof f.rel === "string" && f.rel) out.rel = f.rel;
                if (Array.isArray(f.sel)) out.sel = f.sel.map((s) => String(s));
                return out;
            }),
        };
    }

    async countRecords(modelKey: string, domain: unknown[]): Promise<number | null> {
        const res = await this.http.post<{ count?: number | null }>(
            `/api/web/_model/${slug(modelKey)}/count`,
            { domain } as Json,
        );
        const c = res?.count;
        return typeof c === "number" ? c : null;
    }

    async pickerOptions(modelKey: string, limit = 50): Promise<Array<[string, string]>> {
        try {
            const res = await this.http.post<{ records?: RecordData[] }>(
                `/api/web/_model/${slug(modelKey)}/records/search`,
                { limit } as Json,
            );
            const records = res?.records ?? [];
            return records.map((r) => {
                const rec = (r ?? {}) as Record<string, unknown>;
                const id = String(rec.record_uuid ?? rec.id ?? "");
                const label = String(rec.display_name ?? rec.name ?? id);
                return [id, label] as [string, string];
            }).filter(([id]) => id !== "");
        } catch {
            return [];
        }
    }

    async relatedCounts(queries: RelatedCountQuery[]): Promise<RelatedCountResult[]> {
        if (queries.length === 0) return [];
        const res = await this.http.post<{
            results?: Array<{
                action_key?: string;
                model_key?: string;
                path?: string;
                count?: number;
                lone_uuid?: string | null;
            }>;
        }>(
            `/api/web/_related-counts`,
            {
                queries: queries.map((q) => ({
                    action_key: q.actionKey,
                    active_id: q.activeId,
                    ...(q.activeModelKey ? { active_model_key: q.activeModelKey } : {}),
                })),
            },
        );
        const rows = res?.results ?? [];
        return rows.map((r) => ({
            actionKey: String(r.action_key ?? ""),
            modelKey: String(r.model_key ?? ""),
            path: String(r.path ?? ""),
            count: Number(r.count ?? 0),
            loneUuid: r.lone_uuid ?? null,
        }));
    }
}
